import { useCallback, useSyncExternalStore } from "react";
import { useDataContext } from "../useData";
import { useMainContext } from "../useMainContext";
import { db } from "../../db";
import draft from "./instance";
import { resolveConflicts } from "./resolve";
import { heldMessage, scopeLabel } from "./labels";

const propertyTitle = (id) => db.peek("properties", id)?.title;

// Number of entries (rows changed, created or removed) in the draft of the
// scope. Re-renders only the component that reads it.
export function useDraftCount(scope) {
  return useSyncExternalStore(draft.subscribe, () => draft.count(scope));
}

// The live conflicts of the scope: rows of the draft that someone else saved
// meanwhile ({ accessor, id, action, serverVersion, lastSaved, serverData }).
// The array changes only when the conflicts change.
export function useDraftConflicts(scope) {
  return useSyncExternalStore(draft.subscribe, () => draft.conflicts(scope));
}

export function useAnyDraftDirty() {
  return useSyncExternalStore(draft.subscribe, draft.isAnyDirty);
}

// The conflict of the open form on accessor:id: a server row of its row
// held while the form is dirty (draft.formConflictOf: { accessor, id,
// scope, action, baseVersion, storedVersion, lastSaved }), or null. The
// object changes only when the conflict changes.
export function useFormConflict(accessor, id) {
  return useSyncExternalStore(draft.subscribe, () =>
    accessor && id !== undefined && id !== null
      ? draft.formConflictOf(accessor, id)
      : null
  );
}

// The save of the scope runs (draft.isSaving): Discard, Reload, Rebase and
// the Item's Reset are disabled meanwhile.
export function useDraftSaving(scope) {
  return useSyncExternalStore(draft.subscribe, () =>
    scope ? draft.isSaving(scope) : false
  );
}

// Fetches the stored rows of the conflicts (the 409 has no data) and applies
// them to MemoryDB. Resolves once applied.
export function useReloadItems() {
  const { dataWorker } = useDataContext();

  return useCallback(
    (conflicts = []) =>
      dataWorker.reloadItems(
        conflicts.map(({ type, accessor, id }) => ({
          accessor: type ?? accessor,
          id
        }))
      ),
    [dataWorker]
  );
}

// "Discard my changes and reload" of the conflict dialog (see resolve.js):
// resolve(conflicts) resolves once the stored rows are applied.
export function useResolveConflicts() {
  const reloadItems = useReloadItems();

  return useCallback(
    (conflicts) => resolveConflicts(draft, conflicts, reloadItems),
    [reloadItems]
  );
}

// For an operation that still saves at once (phase 2: add, copy or delete a
// variant) or an explicit save of one row: with unsaved changes in the scope
// it would commit part of the draft with no version check (it reads its rows
// from MemoryDB, i.e. the draft rows), or have the server change rows that
// the draft then writes back; the same for a row it writes that is drafted
// in any other scope. guard(what, rows) is true, with a warning, in those
// cases: the user saves or discards them first. rows: [{ accessor, id,
// label }], the rows the operation writes.
export function useDirtyDraftGuard(scope) {
  const { createNotification } = useMainContext();

  return useCallback(
    (what, rows = []) => {
      const count = draft.count(scope);
      if (count > 0) {
        createNotification(
          "warning",
          `Save or discard the ${count} unsaved change${
            count === 1 ? "" : "s"
          } first: ${what} saves at once.`
        );
        return true;
      }

      for (const row of rows) {
        const holder = draft.ownerOf(row.accessor, row.id);
        if (!holder) continue;

        createNotification(
          "warning",
          `${heldMessage(row, scopeLabel(holder, propertyTitle))} (${what} saves at once)`
        );
        return true;
      }

      return false;
    },
    [createNotification, scope]
  );
}
